import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { FaqBlock, ResultCard, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate, todayISO } from "@/components/age-calculator/premium";
import { AnchorToc, AnchorSection, LifeProgressBar, WeekdayMilestoneGrid, buildAppFaqLd, chronoDiff, CopyResultButton } from "@/components/age-calculator/sub-shell";

const TITLE = "School Admission Age Calculator (India & Pakistan): Nursery to Class 1";
const DESC = "Map your child's age on the school cut-off date (India 1 April / Pakistan 1 August) to the correct class — Nursery, LKG, UKG, Class 1 or KG — with NEP 2020 & PMEF guidance.";

const FAQS = [
  { q: "What is the minimum age for Class 1 admission in India?", a: "As per NEP 2020, a child must have completed 6 years of age on the cut-off date (usually 31 March or 1 April of the admission year) to enter Class 1. Nursery/Pre-KG starts at 3+ years." },
  { q: "What is the school admission cut-off date in Pakistan?", a: "Most Punjab and federal schools use 1 August as the cut-off. Nursery: 3+, Prep/KG: 4+, Class 1: 5+ years on that date." },
  { q: "Can I get age relaxation for early admission?", a: "Some private schools grant ±3 months relaxation with a written parental undertaking. Government CBSE / Punjab Board schools rarely grant relaxation post NEP 2020." },
  { q: "Which class is right for a 5.5-year-old on 1 April?", a: "In India that maps to Senior KG / UKG. A child needs to complete 6 years before the cut-off to enter Class 1 under NEP 2020." },
];

const TOC = [
  { id: "calculator", label: "Calculator" },
  { id: "class-map", label: "Age-to-Class Map" },
  { id: "cutoff", label: "Cut-off by Country" },
  { id: "docs", label: "Documents" },
  { id: "faqs", label: "FAQs" },
];

type Rule = { label: string; min: number; max: number };
const INDIA_RULES: Rule[] = [
  { label: "Nursery / Pre-KG", min: 3, max: 4 },
  { label: "LKG (Jr. KG)", min: 4, max: 5 },
  { label: "UKG (Sr. KG)", min: 5, max: 6 },
  { label: "Class 1", min: 6, max: 7 },
];
const PK_RULES: Rule[] = [
  { label: "Playgroup", min: 2.5, max: 3.5 },
  { label: "Nursery", min: 3.5, max: 4.5 },
  { label: "Prep / KG", min: 4.5, max: 5.5 },
  { label: "Class 1", min: 5.5, max: 6.5 },
];

export const Route = createFileRoute("/age-calculator/school-admission")({
  head: () => leafHead({
    path: "/age-calculator/school-admission",
    title: TITLE, description: DESC,
    scripts: [
      buildAppFaqLd({ name: "School Admission Age Calculator", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "School Admission", url: "https://drhint.com/age-calculator/school-admission" },
      ]),
    ],
  }),
  component: Page,
});

function Page() {
  const y = new Date().getFullYear() + 1;
  const [dob, setDob] = useState("");
  const [country, setCountry] = useState<"IN" | "PK">("IN");
  const [cutoff, setCutoff] = useState(`${y}-04-01`);
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();

  const r = useMemo(() => (dob ? chronoDiff(dob, cutoff) : null), [dob, cutoff]);
  const ageDecimal = r ? r.years + r.months / 12 + r.days / 365 : 0;
  const rules = country === "IN" ? INDIA_RULES : PK_RULES;
  const suggested = rules.find((rr) => ageDecimal >= rr.min && ageDecimal < rr.max);
  const copyText = r ? `Child age on ${cutoff}: ${r.years}y ${r.months}m ${r.days}d → ${suggested?.label ?? "Contact school for placement"}` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / School Admission
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Parents ke liye purpose-built: bacche ki DOB daalein, cut-off date choose karein — hum bataenge ki <strong>Nursery, LKG, UKG ya Class 1</strong> me sahi eligibility kis me banti hai. General maths ke liye <Link to="/age-calculator" className="text-primary underline">core age calculator</Link> par jayein.
        </p>
      </section>

      <AnchorToc items={TOC} />

      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <TripleDateInput label="Child's Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} max={todayISO()} />
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => { setCountry("IN"); setCutoff(`${y}-04-01`); reset(); }} className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${country === "IN" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}>🇮🇳 India (1 Apr)</button>
                <button type="button" onClick={() => { setCountry("PK"); setCutoff(`${y}-08-01`); reset(); }} className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${country === "PK" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}>🇵🇰 Pakistan (1 Aug)</button>
              </div>
            </div>
            <div>
              <TripleDateInput label="Admission Cut-off Date" value={cutoff} onChange={(v) => { setCutoff(v); reset(); }} />
              <div className="mt-3 flex flex-wrap gap-2">
                {[y - 1, y, y + 1].map((yr) => (
                  <button key={yr} type="button" onClick={() => { setCutoff(`${yr}-${country === "IN" ? "04-01" : "08-01"}`); reset(); }} className="px-3 py-1.5 rounded-full text-xs font-bold bg-primary/10 text-primary hover:bg-primary/20">{yr} intake</button>
                ))}
              </div>
            </div>
          </div>
          <AnalyzeButton onClick={trigger} disabled={!dob || analyzing} label="Find Suitable Class" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={260}>
            {revealed && r ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label={`Age on ${cutoff}`} value={`${r.years}y ${r.months}m ${r.days}d`} />
                  <ResultCard label="Decimal Age" value={ageDecimal.toFixed(2) + " yr"} />
                  <ResultCard label="Suggested Class" value={suggested?.label ?? "Discuss with school"} sub={country === "IN" ? "NEP 2020 aligned" : "Punjab / Federal norm"} />
                  <ResultCard label="Country Rule" value={country === "IN" ? "India — 1 April" : "Pakistan — 1 August"} />
                </div>
                <div className="mt-4 flex justify-center"><CopyResultButton text={copyText} label="Copy Placement" /></div>
                <LifeProgressBar dob={dob} expectancy={73} />
                <WeekdayMilestoneGrid dob={dob} />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter child's DOB and cut-off, then tap Find Class.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="class-map" title="Age → Class Mapping (Quick Table)">
        <table>
          <thead><tr><th>Age on Cut-off</th><th>India (NEP)</th><th>Pakistan</th></tr></thead>
          <tbody>
            <tr><td>2.5 – 3.5 yrs</td><td>Pre-Nursery</td><td>Playgroup</td></tr>
            <tr><td>3 – 4 yrs</td><td>Nursery</td><td>Nursery</td></tr>
            <tr><td>4 – 5 yrs</td><td>LKG</td><td>Nursery / Prep</td></tr>
            <tr><td>5 – 6 yrs</td><td>UKG</td><td>Prep / KG</td></tr>
            <tr><td>6+ yrs</td><td>Class 1</td><td>Class 1</td></tr>
          </tbody>
        </table>
      </AnchorSection>

      <AnchorSection id="cutoff" title="Cut-off Date by Country / Board">
        <ul>
          <li><strong>India CBSE / ICSE:</strong> 31 March or 1 April of the admission year.</li>
          <li><strong>Delhi Nursery Admission:</strong> Age band published each November by Directorate of Education.</li>
          <li><strong>Pakistan Federal / Punjab Board:</strong> 1 August is the widely-used cut-off.</li>
          <li><strong>Private schools (both countries):</strong> vary; always confirm from the school's prospectus.</li>
        </ul>
      </AnchorSection>

      <AnchorSection id="docs" title="Documents Schools Typically Ask For">
        <ul>
          <li>Birth certificate (municipal / union council).</li>
          <li>Aadhaar (India) or B-Form (Pakistan).</li>
          <li>Vaccination card.</li>
          <li>Parent photo IDs and address proof.</li>
          <li>Recent passport-size photographs of the child.</li>
        </ul>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}